import React from 'react'
import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow
} from '@material-ui/core'
import * as S from './styles'

export type ColumnDefinition<T> = {
  key: string
  header: string
  width?: string
  centered?: boolean
  render: (row: T, rowIndex: number, sectionIndex: number) => React.ReactNode
}

export type TableSection<T> = {
  title: string
  rows: T[]
  renderAfterSection?: () => React.ReactNode
}

export type RowVariant = 'default' | 'danger'

type Props<T> = {
  columns: ColumnDefinition<T>[]
  sections: TableSection<T>[]
  getRowVariant?: (row: T) => RowVariant
  emptyState?: React.ReactNode
}

function BaseTable<T>({
  columns,
  sections,
  getRowVariant,
  emptyState
}: Props<T>) {
  let globalRowIndex = 0

  const hasRows = sections.some((section) => section.rows.length > 0)

  return (
    <S.Wrapper>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <S.StyledHeaderCell
                  key={column.key}
                  width={column.width}
                  className={column.centered ? 'centered' : ''}
                >
                  {column.header}
                </S.StyledHeaderCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {!hasRows && emptyState ? (
              <TableRow>
                <S.StyledEmptyCell colSpan={columns.length}>
                  {emptyState}
                </S.StyledEmptyCell>
              </TableRow>
            ) : (
              sections.map((section, sectionIndex) => {
                const sectionRows = []

                if (section.title && section.rows.length > 0) {
                  sectionRows.push(
                    <TableRow key={`section-title-${sectionIndex}`}>
                      <S.StyledSectionCell colSpan={columns.length}>
                        {section.title}
                      </S.StyledSectionCell>
                    </TableRow>
                  )
                }

                section.rows.forEach((row, rowIndex) => {
                  const currentGlobalIndex = globalRowIndex++
                  const variant = getRowVariant?.(row) || 'default'
                  sectionRows.push(
                    <S.StyledBodyRow
                      key={`row-${sectionIndex}-${rowIndex}`}
                      $isEven={currentGlobalIndex % 2 === 1}
                      $variant={variant}
                    >
                      {columns.map((column) => (
                        <S.StyledBodyCell
                          key={`${column.key}-${rowIndex}`}
                          className={column.centered ? 'centered' : ''}
                        >
                          {column.render(row, rowIndex, sectionIndex)}
                        </S.StyledBodyCell>
                      ))}
                    </S.StyledBodyRow>
                  )
                })

                if (section.renderAfterSection) {
                  const lastRowIndex = globalRowIndex - 1
                  const lastRow = section.rows[section.rows.length - 1]
                  const variant = lastRow
                    ? getRowVariant?.(lastRow) || 'default'
                    : 'default'
                  sectionRows.push(
                    <S.StyledBodyRow
                      key={`section-after-${sectionIndex}`}
                      $isEven={lastRowIndex % 2 === 1}
                      $variant={variant}
                      $disableHoverColorChange
                    >
                      <S.StyledFullWidthCell colSpan={columns.length}>
                        {section.renderAfterSection()}
                      </S.StyledFullWidthCell>
                    </S.StyledBodyRow>
                  )
                }

                return sectionRows
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </S.Wrapper>
  )
}

export default BaseTable
